import { queryKeys } from "~/lib/query-keys";
import { getBrowserPlatformApiClient } from "~/lib/trpc/browser-client";
import type { PlatformApiTrpcClient } from "~/lib/trpc/platform-api-adapter";

// The eager /build loader imports this module. Allocation preview validation
// lives in allocation-preview-queries so the strategy engine stays route-lazy.

export type DirectIndexCatalog = Awaited<
  ReturnType<PlatformApiTrpcClient["directIndex"]["getCatalog"]["query"]>
>;
export type DirectIndexConstituentsResult = Awaited<
  ReturnType<PlatformApiTrpcClient["directIndex"]["getConstituents"]["query"]>
>;
export type DirectIndexConstituent =
  DirectIndexConstituentsResult["constituents"][number];
export type DirectIndexPreviewResult = Awaited<
  ReturnType<
    PlatformApiTrpcClient["directIndex"]["previewPerformance"]["query"]
  >
>;
export type DirectIndexPreviewRange = DirectIndexPreviewResult["range"];

export interface DirectIndexUniverseInput {
  kind: "index" | "sector";
  id: string;
}

// Structure changes at most daily (index syncs); keep it warm across the
// whole builder session so universe re-selection is instant.
const CATALOG_STALE_TIME_MS = 5 * 60_000;
const CONSTITUENTS_STALE_TIME_MS = 5 * 60_000;

export function getDirectIndexCatalogQueryOptions(input?: {
  platformApi?: PlatformApiTrpcClient;
}) {
  const platformApi = input?.platformApi ?? getBrowserPlatformApiClient();
  return {
    queryKey: queryKeys.directIndex.catalog,
    queryFn: async () => await platformApi.directIndex.getCatalog.query(),
    staleTime: CATALOG_STALE_TIME_MS,
  } as const;
}

export function getDirectIndexConstituentsQueryOptions(input: {
  universe: DirectIndexUniverseInput;
  platformApi?: PlatformApiTrpcClient;
}) {
  const platformApi = input.platformApi ?? getBrowserPlatformApiClient();
  return {
    queryKey: queryKeys.directIndex.constituents(
      input.universe.kind,
      input.universe.id,
    ),
    queryFn: async () =>
      await platformApi.directIndex.getConstituents.query({
        universe: input.universe,
      }),
    staleTime: CONSTITUENTS_STALE_TIME_MS,
  } as const;
}
